import { useAuth } from '@ocula/auth';
import { PageLoading } from '@ocula/ui';
import { useEffect } from 'react';
import { Link, Outlet } from 'react-router-dom';

/**
 * Displays a UI for while the silent login is being attempted by Auth0.
 * In reality this UI is unlikely to be seen by the user because the attempt
 * completes very quickly.
 *
 * This `isLoading` flag in the Auth0Provider context state starts out as `true` and
 * the `isAuthenticated` flag starts out as `false`. For how we use Auth0,
 * the `isLoading` flag becomes `false` in two scenarios:
 * 1. Auth0 has successfully initialised. There may or may not be an authenticated user
 *    at that point. There is an initial `useEffect` in the Auth0Provider that fires
 *    when it is first rendered. This tries to silently log the user in (using `checkSession()`
 *    from the auth0-spa-js package) and then tries to get the user info. If both actions
 *    are successful the user is authenticated. Regardless of whether the user could be
 *    silently authenticated or not, the `isLoading` flag becomes `false`.
 * 2. There was an error, in which case the `error` context state will have a value.
 */
export function SilentLoginInterstitial() {
  const { isLoading, error, logout } = useAuth();

  // TODO use a logging service to capture this error.
  useEffect(() => {
    if (error && import.meta.env.MODE !== 'test') {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [error]);

  if (isLoading) {
    return <PageLoading />;
  }

  if (error) {
    if (error.message === 'invitation not found or already used') {
      // TODO proper UI for this.
      return (
        <div>
          You need a valid invitation link to be able to register with this app. Please contact your organisation&apos;s
          administrator. <Link to="/login">If you are already registered, click here to log in.</Link>
        </div>
      );
    }

    // TODO proper UI for this.
    return (
      <div>
        There was a problem logging you in. Please contact your organisation&apos;s administrator.
        <Link onClick={() => logout({ returnTo: globalThis.location.origin })} to="/login">
          Click here to try logging in again.
        </Link>
      </div>
    );
  }

  return <Outlet />;
}
